import React, { type ReactNode } from 'react'

import { AddButton, Banner, Button } from '@fourthwall/components'
import { PaymentMethodsList } from '@modules/Dashboard/modules/PaymentMethods/components/PaymentMethodsList/PaymentMethodsList'
import * as S from '@modules/Dashboard/modules/PaymentMethods/modules/PaymentMethodRequiredModal/PaymentMethodRequiredModal.styled'

import type { PaymentMethodItem } from '../../PaymentMethods.types'
import { AddPaymentMethod } from '../AddPaymentMethod'
import type {
  ChildrenProps,
  RenderSubmitButtonProps,
  SelectPaymentMethodViewType,
} from './SelectPaymentMethod.types'

export type PaymentMethodRequiredModalViewProps = {
  viewType: SelectPaymentMethodViewType
  submitLabel: string
  withChargeLimit: boolean
  paymentMethodsProps?: PaymentMethodItem[]
  errorBanner?: string
  isSubmitting: boolean
  isLoading: boolean
  isDisabled: boolean
  onSubmit: (paymentMethod?: PaymentMethodItem) => void
  onSuccess: () => void
  onAddPaymentMethod: () => void
  children: (childrenProps: ChildrenProps) => ReactNode
}

export const SelectPaymentMethodView = ({
  viewType,
  submitLabel,
  withChargeLimit,
  paymentMethodsProps,
  errorBanner,
  isSubmitting,
  isDisabled,
  isLoading,
  onAddPaymentMethod,
  onSuccess,
  onSubmit,
  children,
}: PaymentMethodRequiredModalViewProps) => {
  const renderPaymentMethods = () => {
    return (
      <>
        {errorBanner && (
          <S.ErrorBannerContainer>
            <Banner appearance="danger">{errorBanner}</Banner>
          </S.ErrorBannerContainer>
        )}

        {paymentMethodsProps && (
          <S.PaymentMethodsContainer>
            <PaymentMethodsList paymentMethods={paymentMethodsProps} selectable />
          </S.PaymentMethodsContainer>
        )}

        <AddButton label="Add new payment method" onClick={onAddPaymentMethod} />
      </>
    )
  }

  const renderSubmitButton = (options?: RenderSubmitButtonProps) => {
    return (
      <Button
        label={submitLabel}
        appearance="primary"
        loading={isSubmitting || options?.isLoading}
        disabled={isDisabled}
        fullWidth={options?.fullWidth}
        onClick={() => onSubmit()}
      />
    )
  }

  if (viewType === 'LOADING') {
    return children({ renderContent: () => null, renderSubmitButton, isLoading: true })
  }

  if (viewType === 'NO_PAYMENT_METHODS') {
    return (
      <AddPaymentMethod onSuccess={onSuccess} withChargeLimit={withChargeLimit} onSubmit={onSubmit}>
        {({ renderForm, renderSubmitButton, isLoading: isLoadingAddPaymentMethod }) =>
          children({
            renderContent: renderForm,
            renderSubmitButton,
            isLoading: isLoadingAddPaymentMethod || isLoading,
          })
        }
      </AddPaymentMethod>
    )
  }

  // viewType === 'HAS_PAYMENT_METHODS'
  return children({
    renderContent: renderPaymentMethods,
    renderSubmitButton,
    isLoading,
  })
}
